import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

const STYLES = {
  title: { font: { bold: true, size: 18, color: { argb: "FF000080" } }, fill: { type: "pattern", pattern: "solid", fgColor: { argb: "FFDDEBF7" } }, alignment: { horizontal: "center", vertical: "middle" } },
  subtitle: { font: { bold: true, size: 14 }, alignment: { horizontal: "center", vertical: "middle" } },
  dateLine: { font: { italic: true, size: 11, color: { argb: "FF555555" } }, alignment: { horizontal: "left", vertical: "middle" } },
  header: { font: { bold: true, color: { argb: "FFFFFFFF" } }, fill: { type: "pattern", pattern: "solid", fgColor: { argb: "FF4472C4" } }, alignment: { vertical: "middle", horizontal: "center" } },
  data: { font: { bold: false }, alignment: { vertical: "middle", horizontal: "center" } },
  dataLeft: { font: { bold: false }, alignment: { vertical: "middle", horizontal: "left" } },
  branchGroup: { font: { bold: true, size: 14, color: { argb: "FFFFFFFF" } }, fill: { type: "pattern", pattern: "solid", fgColor: { argb: "FF2E75B6" } }, alignment: { vertical: "middle", horizontal: "left" } },
  subtotal: { font: { bold: true, color: { argb: "FF1F4E79" } }, fill: { type: "pattern", pattern: "solid", fgColor: { argb: "FFE2EFDA" } }, alignment: { vertical: "middle", horizontal: "center" } },
  grandTotal: { font: { bold: true, size: 12, color: { argb: "FFFFFFFF" } }, fill: { type: "pattern", pattern: "solid", fgColor: { argb: "FF375623" } }, alignment: { vertical: "middle", horizontal: "center" } },
  zebra: { fill: { type: "pattern", pattern: "solid", fgColor: { argb: "FFF2F2F2" } } },
};

const border = {
  top: { style: "thin" }, left: { style: "thin" }, bottom: { style: "thin" }, right: { style: "thin" },
};

function applyStyle(cell, style) {
  if (style.font) cell.font = { ...cell.font, ...style.font };
  if (style.fill) cell.fill = style.fill;
  if (style.alignment) cell.alignment = style.alignment;
}

function addCell(worksheet, row, col, value, style, isAmount) {
  const cell = worksheet.getCell(row, col);
  cell.value = value;
  cell.border = border;
  applyStyle(cell, style);
  if (isAmount && typeof value === "number") cell.numFmt = "#,##0.00";
}

function formatDate(d) {
  if (!d) return "";
  const date = new Date(d);
  return isNaN(date.getTime()) ? "" : date.toLocaleDateString("en-GB");
}

async function generateLoanProgressExcel(apiData, productHeader) {
  if (!apiData || apiData.length === 0) return;

  const workbook = new ExcelJS.Workbook();
  const ws = workbook.addWorksheet("Loan Application Progress");

  const columns = [
    { header: "Application No.", key: "applicationNumber", width: 16 },
    { header: "Customer No.", key: "customerNumber", width: 16 },
    { header: "Customer Name", key: "customerName", width: 28 },
    { header: "App. Date", key: "appDate", width: 14 },
    { header: "Loan Type", key: "loanTypeName", width: 26 },
    { header: "Requested Amount", key: "requestAmount", width: 18 },
    { header: "Approved Date", key: "approveDate", width: 14 },
    { header: "Dispose Date", key: "loanDisposedDate", width: 14 },
    { header: "Loan Status", key: "status", width: 20 },
    { header: "Branch", key: "branch", width: 22 },
  ];

  const amountCol = 6;

  columns.forEach((c, i) => { ws.getColumn(i + 1).width = c.width; });

  // ── Title section ──
  ws.mergeCells(1, 1, 1, columns.length);
  const titleCell = ws.getCell("A1");
  titleCell.value = "Loan Application Progress Report";
  applyStyle(titleCell, STYLES.title);
  ws.getRow(1).height = 50;

  ws.mergeCells(2, 1, 2, columns.length);
  const subCell = ws.getCell("A2");
  subCell.value = `Loan Product: ${productHeader}`;
  applyStyle(subCell, STYLES.subtitle);
  ws.getRow(2).height = 30;

  ws.mergeCells(3, 1, 3, columns.length);
  const dateCell = ws.getCell("A3");
  dateCell.value = `Printed Date: ${new Date().toLocaleDateString("en-GB")}`;
  applyStyle(dateCell, STYLES.dateLine);

  ws.addRow([]);

  const branchMap = {};
  apiData.forEach(item => {
    const branch = item.branchName || "Unknown";
    if (!branchMap[branch]) branchMap[branch] = [];
    branchMap[branch].push(item);
  });
  const branches = Object.keys(branchMap);

  let currentRow = 5;
  let grandTotal = 0;
  let grandCount = 0;

  branches.forEach((branch, bi) => {
    const items = branchMap[branch];

    // Branch heading
    ws.mergeCells(currentRow, 1, currentRow, columns.length);
    addCell(ws, currentRow, 1, `Branch: ${branch}`, STYLES.branchGroup);
    for (let c = 2; c <= columns.length; c++) addCell(ws, currentRow, c, "", STYLES.branchGroup);
    currentRow++;

    const headerRow = ws.addRow(columns.map(c => c.header));
    headerRow.eachCell((cell) => { applyStyle(cell, STYLES.header); cell.border = border; });
    headerRow.height = 22;
    currentRow++;

    let branchTotal = 0;
    let branchCount = 0;

    items.forEach((item, idx) => {
      const rla = Number(item.requestAmount) || 0;
      const branch = item.branchName || "Unknown";
      branchTotal += rla;
      branchCount++;
      grandTotal += rla;
      grandCount++;

      const vals = [
        item.applicationNumber || item.loanNo || "",
        item.customerNumber || "",
        item.customerName || "",
        formatDate(item.appDate),
        item.loanTypeName || item.productName || "",
        rla,
        formatDate(item.approveDate),
        formatDate(item.loanDisposedDate),
        item.status || "",
        branch,
      ];

      const dataRow = ws.addRow(vals);
      dataRow.eachCell((cell, colNum) => {
        cell.border = border;
        const style = (colNum === 3 || colNum === 5 || colNum === 10) ? STYLES.dataLeft : STYLES.data;
        applyStyle(cell, style);
        if (colNum === amountCol && typeof cell.value === "number") cell.numFmt = "#,##0.00";
      });
      if (idx % 2 === 1) {
        dataRow.eachCell((cell) => { if (!cell.fill) cell.fill = STYLES.zebra.fill; });
      }
      dataRow.height = 20;
      currentRow++;
    });

    // Branch total
    ws.mergeCells(currentRow, 1, currentRow, 5);
    addCell(ws, currentRow, 1, `Branch Total: ${branch}`, STYLES.subtotal);
    addCell(ws, currentRow, amountCol, branchTotal, STYLES.subtotal, true);
    for (let c = 7; c <= columns.length; c++) addCell(ws, currentRow, c, "", STYLES.subtotal);
    currentRow++;

    // Blank separator between branch tables
    if (bi < branches.length - 1) currentRow++;
  });

  // ── Grand Total ──
  ws.mergeCells(currentRow, 1, currentRow, 5);
  addCell(ws, currentRow, 1, "GRAND TOTAL", STYLES.grandTotal);
  addCell(ws, currentRow, amountCol, grandTotal, STYLES.grandTotal, true);
  for (let c = 7; c <= columns.length; c++) addCell(ws, currentRow, c, "", STYLES.grandTotal);
  currentRow += 2;

  // ── Summary ──
  ws.mergeCells(currentRow, 1, currentRow, columns.length);
  addCell(ws, currentRow, 1, "Summary", { font: { bold: true, size: 14 } });
  currentRow++;

  const summaryItems = [
    ["Total Requested Amount", grandTotal],
    ["Total Records", grandCount],
  ];

  summaryItems.forEach(([label, value]) => {
    addCell(ws, currentRow, 1, label, { font: { bold: true }, alignment: { vertical: "middle", horizontal: "right" } });
    ws.mergeCells(currentRow, 1, currentRow, 3);
    const valCell = ws.getCell(currentRow, 4);
    valCell.value = value;
    valCell.border = border;
    valCell.font = { bold: true, size: 12, color: { argb: "FF000080" } };
    valCell.numFmt = typeof value === "number" ? "#,##0.00" : undefined;
    valCell.alignment = { vertical: "middle", horizontal: "left" };
    ws.getRow(currentRow).height = 22;
    currentRow++;
  });

  ws.views = [{ state: "frozen", xSplit: 3, ySplit: 5 }];

  const buffer = await workbook.xlsx.writeBuffer();
  const now = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  const finalFileName = `LoanApplicationProgress_${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}_${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}.xlsx`;
  saveAs(new Blob([buffer]), finalFileName);
}

export default generateLoanProgressExcel;
